import React, { FunctionComponent, useRef } from 'react'
import { makeStyles } from '@material-ui/styles'
import useParams from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import FontAwesomeIcon from './FontAwesomeIcon'
import clsx from 'clsx'

interface Props {
  className?: string
  copyText: string
  children?: any
  inline?: boolean
  codeBlock?: boolean
  header?: string
  // fill property for if you want the copy to clipboard container to fill
  // the entire width of the parent, but still want the copy to clipboard
  // icon inline
  fill?: boolean
  copyIcon?: any
  triggerWithChild?: boolean
}

type State = {
  isCopySuccessful: boolean
}

const defaultParams: State = {
  isCopySuccessful: false,
}

interface StyleProps {
  codeBlock?: boolean
  inline?: boolean
  fill?: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  textArea: {
    position: 'absolute',
    top: '-9999px',
    left: '-9999px',
  },
  copyButton: {
    padding: '0 0 0 2px',
  },
  copyContainer: {
    display: ({ fill, inline }) => (fill ? 'flex' : inline ? 'inline-flex' : 'flex'),
    flexDirection: ({ inline }) => (inline ? 'row' : 'column'),
    border: `1px solid ${theme.components.code.border}`,
    borderRadius: 4,
    width: '100%',

    '& pre': {
      flexGrow: 1,
      borderRadius: ({ codeBlock }) => (codeBlock ? '4px 0 0 4px' : 4),
    },
  },
  copyIconContainer: {
    padding: '0 8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    background: 'transparent',
    borderLeft: `1px solid ${theme.components.code.border}`,
    borderRadius: ({ codeBlock }) => (codeBlock ? '0 4px 4px 0' : 0),
    left: ({ codeBlock }) => (codeBlock ? -2 : 0),
  },
  copyIcon: {
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  done: {
    fontWeight: 400,
    color: theme.palette.green.main,
  },
  copy: {
    fontWeight: 400,
    color: ({ codeBlock }) => (codeBlock ? theme.components.code.text : theme.palette.primary.main),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.components.code.text,
    padding: theme.spacing(0, 2),
    position: 'relative',
    '& > p': {
      ...theme.typography.subtitle2,
      fontWeight: 'bold',
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: theme.spacing(),
      right: theme.spacing(),
      height: 2,
      background: ({ codeBlock }) => (codeBlock ? theme.components.code.text : 'transparent'),
    },
  },
}))

const CopyToClipboard: FunctionComponent<Props> = ({
  className,
  children,
  copyText,
  inline = true,
  codeBlock = true,
  header = undefined,
  fill = false,
  triggerWithChild = false,
}) => {
  const { params, updateParams } = useParams<State>(defaultParams)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const classes = useStyles({ codeBlock, inline, fill })

  const handleCopy = (e) => {
    e.stopPropagation()
    try {
      const selected =
        document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false
      textAreaRef.current.select()
      const success = document.execCommand('copy')

      if (!success) {
        throw new Error('Unable to copy text')
      }

      if (selected) {
        document.getSelection().removeAllRanges()
        document.getSelection().addRange(selected)
      }

      updateParams({ isCopySuccessful: true })
      setTimeout(() => {
        updateParams({ isCopySuccessful: false })
      }, 2000)
    } catch (e) {
      console.log('Unable to copy text')
    }
  }

  const resolvedChildren =
    typeof children === 'function'
      ? children({ isCopySuccessful: params.isCopySuccessful })
      : children

  const copyActionElems = (
    <div className={classes.copyIconContainer} onClick={handleCopy}>
      <FontAwesomeIcon
        size="md"
        className={clsx(
          classes.copyIcon,
          params.isCopySuccessful ? classes.done : `${classes.copy} copy`,
        )}
      >
        {params.isCopySuccessful ? 'check' : 'copy'}
      </FontAwesomeIcon>
    </div>
  )

  // readOnly is needed in textarea to silence React warning about missing onChange
  return triggerWithChild ? (
    <div className={className} onClick={handleCopy}>
      <textarea ref={textAreaRef} value={copyText} className={classes.textArea} readOnly />
      {resolvedChildren}
    </div>
  ) : (
    <div className={clsx(classes.copyContainer, className)}>
      <textarea ref={textAreaRef} value={copyText} className={classes.textArea} readOnly />
      {!!header && (
        <div className={classes.header}>
          <p>{header}</p>
          {!inline && copyActionElems}
        </div>
      )}
      {resolvedChildren}
      {inline && copyActionElems}
    </div>
  )
}

export default CopyToClipboard
